/**
 * ============================================================================
 * Expense Classifier — Auto-clasificador de Base de Prorrateo
 * ============================================================================
 *
 * Heurística determinística (regex/keyword) que sugiere si un gasto debe
 * prorratearse por **Peso** o por **Valor**, basándose en su concepto.
 *
 * B-03 (CA-04/05/06):
 *   - Surcharges marítimos (BAF, CAF, EIS, IMO, PSS, LSS…) → Peso
 *   - Almacenaje portuario / bodegaje / warehouse / storage → Peso
 *     (se cobran por peso/volumen, NO por valor de carga)
 *   - Sin match → "SIN_CLASIFICAR" (estado explícito que bloquea guardado)
 *     en lugar del peligroso fallback silencioso a "Valor".
 *
 * Se usa en:
 *   - Frontend: AddGastoForm / EditGastoSheet / ExpenseManagerSheet
 *   - Edge function: ingest-document (preset al recibir gastos OCR de n8n)
 *
 * Confianza:
 *   - "keyword": match directo a un keyword (la UI puede pre-seleccionar)
 *   - "none":    no hay match (la UI debe pedir intervención humana)
 * ============================================================================
 */

import type { BaseProrrateo } from "@/hooks/useGastosEmbarque";

// ── F-05: Documentation fees en origen → Valor + forzosamente gravables ──
// Estos cargos forman parte del CIF DIAN (gastos en origen / pre-shipment)
// y deben sumar a la base sobre la que se calcula Arancel + IVA.
const GRAVABLE_VALOR_KEYWORDS = [
  "doc fee", "docs fee", "documentation fee", "documentation charge",
  "origin doc", "origin documentation",
  "bl fee", "b/l fee", "bill of lading fee",
  "ams", "isf", "ens",
  "telex release", "telex",
  "courier fee origen", "courier origen",
];

// Palabras clave que disparan PESO (transporte físico + surcharges marítimos
// + almacenamiento portuario, todos cobrados por peso/volumen).
const PESO_KEYWORDS = [
  // Transporte físico
  "flete", "freight", "transporte", "acarreo", "trucking",
  "maritim", "maritimo", "marítim", "ocean", "oceanic",
  "aereo", "aéreo", "air freight", "airfreight",
  "terrestre", "cabotaje", "drayage",
  "thc", "isps", "bunker",
  // B-03 FIX 1 — Surcharges marítimos (todos prorratean por peso)
  "baf", "caf", "eis", "imo", "pss", "lss", "wrs", "srf",
  "bunker adjustment",
  // B-03 FIX 2 — Almacenamiento / movilización portuaria (cobro por peso/volumen)
  "almacena", "warehouse", "bodegaje", "storage",
  "movilizacion de carga", "movilización de carga",
];

// Palabras clave que disparan VALOR (servicios financieros / seguros /
// honorarios profesionales). NOTA: se removió el genérico "fee" porque
// genera demasiados falsos positivos (handling fee, late fee, etc.) — los
// fees específicos siguen capturados por GRAVABLE_VALOR_KEYWORDS.
const VALOR_KEYWORDS = [
  "seguro", "insurance",
  "honorario", "comision", "comisión", "commission",
  "agenciamiento", "agency", "brokerage",
  "financiero", "bancario", "interes", "interés",
  "arancel", "iva", "impuesto", "tributo", "tax",
];

/**
 * B-03 — Estado explícito de "no clasificado". La UI debe forzar HITL
 * (Human-In-The-Loop) y bloquear el guardado hasta que el usuario elija.
 */
export type BaseProrrateoOrUnclassified = BaseProrrateo | "SIN_CLASIFICAR";

export interface ClassificationResult {
  /**
   * B-03 — Base sugerida explícita. Nunca null: usar "SIN_CLASIFICAR" cuando
   * no hay match para forzar la decisión humana.
   */
  base_prorrateo: BaseProrrateoOrUnclassified;
  /**
   * @deprecated B-03 — Mantener en sincronía con `base_prorrateo` para
   * compatibilidad con código pre-B-03 (`null` cuando no hay match).
   */
  suggested: BaseProrrateo | null;
  /** Palabra clave detectada (para tooltip / notas). */
  matchedKeyword: string | null;
  /** "keyword" si hubo match; "none" → fuerza SIN_CLASIFICAR. */
  confidence: "keyword" | "none";
  /**
   * F-05: Si `true`, el gasto debe marcarse `incluir_en_base_gravable=true`
   * por defecto (gasto en origen que forma parte del CIF DIAN).
   * El usuario puede destildarlo manualmente (HITL).
   */
  forceGravable?: boolean;
}

/**
 * Devuelve la base sugerida para un concepto. No muta nada.
 *
 * Orden de evaluación:
 *   1. GRAVABLE_VALOR (Doc Fees) — más específicos, deben matchear primero
 *   2. PESO (transporte físico + surcharges + almacenamiento)
 *   3. VALOR (servicios financieros)
 *   4. fallback → "SIN_CLASIFICAR" (B-03)
 */
export function classifyExpense(concepto: string | null | undefined): ClassificationResult {
  if (!concepto || typeof concepto !== "string" || !concepto.trim()) {
    return {
      base_prorrateo: "SIN_CLASIFICAR",
      suggested: null,
      matchedKeyword: null,
      confidence: "none",
    };
  }

  const normalized = concepto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // strip accents

  // 1. F-05: Documentation fees → Valor + gravable
  for (const kw of GRAVABLE_VALOR_KEYWORDS) {
    if (normalized.includes(kw)) {
      return {
        base_prorrateo: "Valor",
        suggested: "Valor",
        matchedKeyword: kw,
        confidence: "keyword",
        forceGravable: true,
      };
    }
  }

  // 2. Peso (incluye surcharges + almacenamiento por B-03)
  for (const kw of PESO_KEYWORDS) {
    if (normalized.includes(kw)) {
      return {
        base_prorrateo: "Peso",
        suggested: "Peso",
        matchedKeyword: kw,
        confidence: "keyword",
      };
    }
  }

  // 3. Valor (sin gravable forzoso)
  for (const kw of VALOR_KEYWORDS) {
    if (normalized.includes(kw)) {
      return {
        base_prorrateo: "Valor",
        suggested: "Valor",
        matchedKeyword: kw,
        confidence: "keyword",
      };
    }
  }

  // 4. B-03 — fallback explícito (NO silencioso a "Valor")
  return {
    base_prorrateo: "SIN_CLASIFICAR",
    suggested: null,
    matchedKeyword: null,
    confidence: "none",
  };
}

/**
 * Sufijo de telemetría para concatenar al campo `notas` cuando la
 * sugerencia se aplicó automáticamente (ingest webhook).
 */
export function buildClassificationNote(result: ClassificationResult): string {
  if (result.confidence === "none") return "";
  const gravable = result.forceGravable ? " · gravable DIAN" : "";
  return `[Auto-clasificado a ${result.base_prorrateo}${result.matchedKeyword ? ` por "${result.matchedKeyword}"` : ""}${gravable}]`;
}
